import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, IAccount } from '@services/accounts.service';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { IDataPoint } from '@services/datapoints-data.service';
import { IIntegratonSensor } from '@services/integration-sensors.service';
import { IntegrationsService, IWebhookIntegration } from '@services/integrations.service';
import { ListView } from '@syncfusion/ej2-angular-lists';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, takeUntil, tap } from 'rxjs/operators';
import { NeuronsensorsService, nsISystem, nsPostIntegration, nsWebhookConfig } from '@services/neuronsensors.service';
import { MatSlideToggle } from '@angular/material/slide-toggle';

/**
 * Component
 */
@Component({
	selector: 'app-data-integrations-neuronsensors',
	templateUrl: './data-integrations-neuronsensors.component.html',
	styleUrls: ['./data-integrations-neuronsensors.component.scss']
})
export class DataIntegrationsNeuronsensorsComponent implements OnInit, OnDestroy {

	/** Route Organisation ID */
	public routeOrganisationID: string

	/** Selected Account GUID */
	public selectedAccountGUID: string

	/** Selected Account */
	public selectedAccount: IAccount

	/** Route Webhook ID */
	@Input() routeWebhookID: string

	/** Webhook Integration */
	public webhookIntegration: IWebhookIntegration

	/** Current User */
	public currentUser: IWebAppUser

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Array of Sensors */
	public sensorsList: IIntegratonSensor[]

	/** filtered Sensors List */
	public filteredSensorsList: IIntegratonSensor[]

	/** Selected Sensor */
	public selectedSensor: IIntegratonSensor;

	/** Sensors Datapoints */
	public sensorDatapoints: IDataPoint[]

	@ViewChild('listviewInstance') public listviewInstance: ListView;
	public dataSource: object;
	public cssClass: string = 'e-list-template';
	isMobile: boolean;
	count: number = 0;

	public NeuronSystem: any

	public NeuronWebhook: any
	public NeuronWebhookHeaders: [string, string][]

	/** switchWebhookEnabled */
	@ViewChild('switchWebhookEnabled') switchWebhookEnabled: MatSlideToggle;

	/**
	 * Default Constructor
	 * @param globalFunctions Global Functions
	 * @param authenticationService Authentication Services
	 * @param activatedRoute ActivatedRoute
	 * @param siteSubscriptions siteSubscriptions
	 * @param integrationService integrationService
	 * @param neuronsensorsService neuronsensorsService
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private activatedRoute: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private integrationService: IntegrationsService,
		private neuronsensorsService: NeuronsensorsService,
	) {

		if (this.activatedRoute.parent) {
			combineLatest([
				this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.activatedRoute.parent.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.activatedRoute.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
			]).pipe(
				// Ensure that subscriptions are not null or undefined
				filter(([user, account, parentParams, params]) => !!user && !!account && !!parentParams && !!params)
			).subscribe({
				next: ([user, account, parentParams, params]) => {
					this.currentUser = <IWebAppUser>user
					this.selectedAccount = account;
					this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
					this.selectedAccountGUID = this.selectedAccount.GUID;
					this.routeOrganisationID = parentParams.get('organisation') ?? ""
					this.routeWebhookID = params.get('integrationID') ?? ""

					this.getWebhookInfo(this.selectedAccountGUID, this.routeOrganisationID, this.routeWebhookID);

				},
				error: (error) => {
					this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
				}
			});
		}
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.isMobile = window.matchMedia('(max-width:550px)').matches;
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * getWebhookInfo
	 */
	getWebhookInfo(selectedAccountGUID: string, routeOrganisationID: string, routeWebhookID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.integrationService.getWebhookIntegrations$(selectedAccountGUID, routeOrganisationID, routeWebhookID).pipe(
			tap(() => {
				this.webhookIntegration = <IWebhookIntegration>{}
				this.NeuronSystem = undefined
				this.NeuronWebhook = undefined
				this.NeuronWebhookHeaders = []
			}),
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((integration) => !!integration),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (integration) => {
				this.webhookIntegration = <IWebhookIntegration>integration
				this.getNeuronSensorDetals(this.webhookIntegration.NeuronSensorAPI,
					this.webhookIntegration.NeuronSensorSystem,
					this.webhookIntegration.ClientWebhookID)
			},
			error: (error) => {
				this.webhookIntegration = <IWebhookIntegration>{}
			}
		})

	}

	/**
	 * Toggle the enable of NeuronSensor Webhook
	 * @param event 
	 */
	nsEnable(event: MouseEvent) {

		// Prevent the toggle from changing immediately
		event.preventDefault();
		event.stopPropagation();

		// Check if webhook is enabled
		const enabled = this.NeuronWebhook.disabled < 100;

		this.globalFunctions.UserConfirm$(`${enabled ? "Disable" : "Enable"} this webhook on NeuronSensors?`)
			.subscribe(confirmed => {
				if (confirmed) {
					// Manually toggle
					this.NeuronWebhook.disabled = enabled ? 100 : 0;

					// Update the toggle UI
					this.switchWebhookEnabled.checked = !enabled;

					// Optionally patch via API
					this.patchWebhook({ ...this.NeuronWebhook });
				}
			});
	}

	/**
		 * patchWebhook
		 * @param _edit 
		 */
	patchWebhook(_edit: nsWebhookConfig) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();
		var apiKey = this.webhookIntegration.NeuronSensorAPI
		var systemID = this.webhookIntegration.NeuronSensorSystem

		this.neuronsensorsService.patchIntegration$(apiKey, systemID, _edit).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((success) => !!success),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (success) => {
				if (this.NeuronWebhook) {
					Object.assign(this.NeuronWebhook, _edit);
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error patching Webhook", true);
			}
		})
	}

	/**
	 * getNeuronSensorDetals
	 * @param apiKey
	 * @param systemId
	 * @param id
	 */
	getNeuronSensorDetals(apiKey: string, systemId: string, id: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		try {
			combineLatest([
				this.neuronsensorsService.getSystems$(apiKey, systemId).pipe(takeUntil(fetchDataSubject), distinctUntilChanged()),
				this.neuronsensorsService.getIntegrations$(apiKey, systemId, id).pipe(takeUntil(fetchDataSubject), distinctUntilChanged())
			]).pipe(
				// Ensure that subscriptions are not null or undefined
				filter(([system, integration]) => !!system && !!integration),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: ([system, integration]) => {
					try {
						this.NeuronSystem = <nsISystem>system;
						this.NeuronWebhook = <nsPostIntegration>integration;
						this.NeuronWebhookHeaders = Object.entries(this.NeuronWebhook.headers);
					}
					catch (error) { console.error(error); }
				},
				error: (error) => {
					if (error.status !== 404) {
						this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
					}
				}
			});
		} catch (error) { console.error(error); }

	}

	/**
	 * goToWebhook
	 */
	goToSystem(path: string = "") {
		// window.open(`https://neuronsensors.app/systems/${this.webhookIntegration?.NeuronSensorSystem}/integrations/${this.webhookIntegration?.ClientWebhookID}`);

		if (this.NeuronSystem) {
			const emailToCheck = this.currentUser.Email;
			const exists = this.NeuronSystem.users.some(
				(user: string) => user.toLowerCase() === emailToCheck.toLowerCase()
			);
			console.log(exists); // true
		}

		window.open(`https://neuronsensors.app/systems/${this.webhookIntegration?.NeuronSensorSystem}/${path}`);
	}

}
